/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useSelector } from "react-redux"
import { useHistory } from "react-router-dom"

import { HOME } from "constants/routes"
import { EcommerceCenteredSpin } from "ecommerce/components/EcommerceCenteredSpin/EcommerceCenteredSpin"
import { MartEcommCatalogMigrationErrorState } from "ecommerce/components/MartEcommCatalogMigrationErrorState"
import { useEcomMigrationCSV } from "ecommerce/hooks/useEcomMigrationCSV"
import {
  isPendingEcomMigrationReview,
  parseEcomMigrationTimestamp,
} from "ecommerce/utils/ecomMigration"
import { MexcoreapipbGetEcomMigrationCSVResponse } from "generated/mexcoreapi-sellercenter.type"
import { AppState } from "store/store.type"

interface MartEcommCatalogMigrationContextValue {
  /** The accepted metadata response — consumers must not refetch it. */
  migrationCsvData: MexcoreapipbGetEcomMigrationCSVResponse
  refetchMigrationCsv: () => Promise<MexcoreapipbGetEcomMigrationCSVResponse>
}

const MartEcommCatalogMigrationContext = createContext<
  MartEcommCatalogMigrationContextValue | undefined
>(undefined)

export const useMartEcommCatalogMigration = (): MartEcommCatalogMigrationContextValue => {
  const value = useContext(MartEcommCatalogMigrationContext)
  if (!value) {
    throw new Error(
      "useMartEcommCatalogMigration must be used within MartEcommCatalogMigrationRouteGate",
    )
  }
  return value
}

const MAX_TIMEOUT_MS = 2_147_483_647

interface MartEcommCatalogMigrationRouteGateProps {
  children: ReactNode
}

/**
 * Guards the /mart-ecomm-catalog-migration route tree. Mounted once above both
 * the intro and review routes, so navigating between them keeps this single
 * getEcomMigrationCSV subscription alive — no second metadata request.
 *
 * Rendering blocks until the first metadata request resolves; migration pages
 * render only for an unexpired PENDING_REVIEW state, and anything else
 * redirects home without flashing the protected content.
 */
export const MartEcommCatalogMigrationRouteGate: FC<MartEcommCatalogMigrationRouteGateProps> = ({
  children,
}) => {
  const history = useHistory()
  const merchantGroupID = useSelector(
    (state: AppState) => state.menu.merchantGroupID,
  )
  const {
    migrationCsvData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useEcomMigrationCSV({ merchantGroupID, isEligibleSurface: true })

  const [, setReviewDeadlineVersion] = useState(0)
  const [isRecoveringFromError, setIsRecoveringFromError] = useState(false)
  const reviewEndAtMs = parseEcomMigrationTimestamp(
    migrationCsvData?.reviewEndAt ?? "",
  )

  const isPendingReview = isPendingEcomMigrationReview(migrationCsvData)

  useEffect(() => {
    if (reviewEndAtMs === null || reviewEndAtMs <= Date.now()) {
      return
    }

    let timeoutID: number | undefined
    const revalidateAtDeadline = () => {
      const remainingMs = reviewEndAtMs - Date.now()
      if (remainingMs <= 0) {
        setReviewDeadlineVersion(previous => previous + 1)
        return
      }
      timeoutID = window.setTimeout(
        revalidateAtDeadline,
        Math.min(remainingMs, MAX_TIMEOUT_MS),
      )
    }

    revalidateAtDeadline()

    // eslint-disable-next-line consistent-return
    return () => {
      if (timeoutID !== undefined) {
        window.clearTimeout(timeoutID)
      }
    }
  }, [reviewEndAtMs])

  useEffect(() => {
    if (
      !isError &&
      !isRecoveringFromError &&
      migrationCsvData &&
      !isPendingReview
    ) {
      history.replace(HOME)
    }
  }, [
    migrationCsvData,
    isPendingReview,
    isError,
    isRecoveringFromError,
    history,
  ])

  useEffect(() => {
    if (isRecoveringFromError && !isFetching && !isError) {
      setIsRecoveringFromError(false)
    }
  }, [isRecoveringFromError, isFetching, isError])

  const retryAfterError = () => {
    setIsRecoveringFromError(true)
    refetch().catch(() => undefined)
  }

  const contextValue = useMemo(
    () =>
      migrationCsvData
        ? { migrationCsvData, refetchMigrationCsv: refetch }
        : undefined,
    [migrationCsvData, refetch],
  )

  // A failed refresh can retain the previous RTK Query currentData. The error
  // must take precedence so stale metadata and its presigned URL are not used.
  if (isError && !isLoading) {
    return <MartEcommCatalogMigrationErrorState onRetry={retryAfterError} />
  }

  // RTK Query keeps currentData while retrying. Keep the gate closed until the
  // retry succeeds so the review page cannot download a stale presigned URL.
  if (isRecoveringFromError) {
    return <EcommerceCenteredSpin />
  }

  if (!migrationCsvData || !isPendingReview) {
    return <EcommerceCenteredSpin />
  }

  return (
    <MartEcommCatalogMigrationContext.Provider value={contextValue}>
      {children}
    </MartEcommCatalogMigrationContext.Provider>
  )
}
